import React, { useState, useRef, useEffect } from "react";
import Compressor from "compressorjs";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ImageUploading from "react-images-uploading";
import sanitizeFilename from "sanitize-filename";

import styles from "../styles/components/reclamation.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faXmark,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import PhotoPickerDialog from "./PhotoPickerDialog";
import { set } from "date-fns";

// Component which allows user to select from gallery or take an image and join it to form
export const ImageUploader = ({ setValue, getValues, onScroll }) => {
  const [images, setImages] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const maxNumber = 2; // Max number of images allowed
  const [openDialog, setOpenDialog] = useState(false);
  const [userChoice, setUserChoice] = useState(null);
  const uploadRef = useRef(null);

  // Add a key to force re-render of ImageUploading component
  const [uploadKey, setUploadKey] = useState(0);

  // Load images from localStorage when component mounts
  // useEffect(() => {
  //   const savedData = JSON.parse(localStorage.getItem('formData'));
  //   if (savedData) {
  //     const compressedFiles = savedData.compressedFiles;
  //     if (compressedFiles && compressedFiles.length !== 0) {
  //       const loadedImages = compressedFiles.map((fileData, index) => ({
  //         data_url: fileData.base64, // Use base64 data
  //         file: new File([], fileData.name, {
  //           type: fileData.type,
  //           lastModified: fileData.lastModified,
  //         }),
  //         id: `${fileData.name}_${index}`,
  //         compressed: true, // Mark as already compressed
  //       }));
  //       setImages(loadedImages);
  //     }
  //   }
  // }, []);

  // Save form data to localStorage on change
  useEffect(() => {
    if (images.length > 0) {
      const compressedFiles = images.map((image) => ({
        base64: image.data_url,
        name: image.file.name,
        type: image.file.type,
        lastModified: image.file.lastModified,
      }));

      const savedData = JSON.parse(localStorage.getItem("formData")) || {};
      const updatedData = {
        ...savedData,
        compressedFiles,
      };

      localStorage.setItem("formData", JSON.stringify(updatedData));
    }
  }, [images]);

  const displayAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: message,
    });
  };

  const checkImages = (imageList) => {
    const validImages = [];
    imageList.forEach((image) => {
      const safeFilename = sanitizeFilename(image.file.name);
      if (!image.file.type.startsWith("image/")) {
        displayAlert("Le fichier sélectionné n'est pas valide");
      } else if (safeFilename !== image.file.name) {
        displayAlert("Le nom du fichier contient des caractères non valides");
      } else {
        validImages.push(image);
      }
    });
    return validImages;
  };

  const handleImageChange = async (imageList) => {
    console.log("handleImageChange called with:", imageList.length, "images");
    console.log("Current images in state:", images.length);

    // Validate all images first
    const validImages = checkImages(imageList);
    if (validImages.length !== imageList.length) {
      return; // Stop processing if there are invalid images
    }

    setIsCompressing(true);

    // Find the newly added images by comparing with the current images in state
    const newImages = imageList.filter(
      (image) =>
        !images.some(
          (img) =>
            img.file.name === image.file.name &&
            img.file.size === image.file.size
        )
    );

    console.log("New images to process:", newImages.length);

    if (newImages.length === 0) {
      setIsCompressing(false);
      return;
    }

    try {
      // Validate and compress the new images
      const base64Files = await Promise.all(
        newImages.map(async (image) => {
          if (image.compressed) {
            // If the image is already compressed, skip compression
            return image;
          }

          try {
            // Compress the image and convert it to base64
            const compressedFile = await compressImage(image.file);

            // Create a base64 representation of the compressed image
            const reader = new FileReader();
            return new Promise((resolve, reject) => {
              reader.onload = () => {
                resolve({
                  base64: reader.result, // Base64 data
                  file: compressedFile, // Compressed file object
                  compressed: true,
                });
              };
              reader.onerror = () => reject("Error reading file");
              reader.readAsDataURL(compressedFile); // Convert to base64
            });
          } catch (error) {
            console.error("Error during compression:", error);
            throw error;
          }
        })
      );

      // Add only the newly processed images to the state
      setImages((prevImages) => {
        const updatedImages = [
          ...prevImages, // Existing images
          ...base64Files.map((fileData, index) => ({
            data_url: fileData.base64, // Base64 for preview
            file: fileData.file, // Compressed file object
            id: `${fileData.file.name}_${Date.now()}_${index}`, // More unique ID
            compressed: true,
          })),
        ];

        console.log("Updated images:", updatedImages.length);

        // Update the form value with the compressed files (base64 and metadata)
        setValue("compressedFiles", [
          ...updatedImages.map((fileData) => ({
            base64: fileData.data_url,
            name: fileData.file.name,
            type: fileData.file.type,
            lastModified: fileData.file.lastModified,
          })),
        ]);

        return updatedImages;
      });
    } catch (error) {
      console.error("Error processing images:", error);
      displayAlert("Erreur lors du traitement de l'image");
    } finally {
      setIsCompressing(false);
      // Reset user choice after processing
      setUserChoice(null);
      // Force re-render of ImageUploading component
      setUploadKey((prev) => prev + 1);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.6,
        maxWidth: 800,
        maxHeight: 800,
        mimeType: "image/jpeg",
        success: (compressedFile) => resolve(compressedFile),
        error: (error) => reject(error),
      });
    });
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    const currentCompressedFiles = getValues("compressedFiles") || [];
    setValue(
      "compressedFiles",
      currentCompressedFiles.filter((_, i) => i !== index)
    );
    // Force re-render after removal
    setUploadKey((prev) => prev + 1);
  };

  const handleOpenDialog = (uploadFunction) => {
    if (isMobile) {
      uploadRef.current = uploadFunction;
      setOpenDialog(true);
    } else {
      uploadFunction();
    }
  };

  const onPickCamera = () => {
    console.log("Camera selected");
    setUserChoice("camera");
    setOpenDialog(false);
    // Small delay to ensure state is updated before triggering upload
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current();
      }
    }, 100);
  };

  const onPickGallery = () => {
    console.log("Gallery selected");
    setUserChoice("gallery");
    setOpenDialog(false);
    // Small delay to ensure state is updated before triggering upload
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current();
      }
    }, 100);
  };

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    setIsMobile(mobileRegex.test(userAgent));
  }, []);

  // Remove the automatic trigger effect - handle it directly in the picker functions
  // useEffect(() => {
  //   if(userChoice){
  //     uploadRef.current && uploadRef.current();
  //     setUserChoice(null);
  //   }
  // },[userChoice]);

  const getInputProps = () => {
    const baseProps = { accept: "image/*" };

    if (userChoice === "camera") {
      return {
        ...baseProps,
        capture: "environment", // This tells iOS to prefer the camera
      };
    }

    return baseProps;
  };

  return (
    <>
      <div
        className={classNames(
          i18n.language === "fr" ||  i18n.language === "en" ? styles.imagesField : styles.imagesFieldArabic
        )}
      >
        <ImageUploading
          key={uploadKey} // Force re-render when key changes
          multiple // To accept multiple images
          value={images}
          onChange={handleImageChange}
          maxNumber={maxNumber} // accepts maxNumber images
          dataURLKey="data_url"
          acceptType={["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "avif", "bmp", "tiff"]}
          inputProps={getInputProps()}
        >
          {({ imageList, onImageUpload, onImageRemove }) => (
            <div className={styles.imagePreviews}>
              {/* Images previews */}
              {imageList.map((image, index) => (
                <div
                  key={`${image.id || index}_${uploadKey}`}
                  className={styles.buttonContainer}
                >
                  <div className={styles.imageButton}>
                    <img
                      src={image.data_url}
                      alt={`Preview ${index}`}
                      className={styles.previewImage}
                    />
                    {isCompressing && (
                      <div className={styles.loadingOverlay}>
                        <FontAwesomeIcon
                          icon={faSpinner}
                          className={styles.spinnerIcon}
                          spin
                        />
                      </div>
                    )}
                    {/* Remove an inserted image */}
                    <span
                      className={styles.removeBtn}
                      onClick={() => handleRemoveImage(index)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </span>
                  </div>
                </div>
              ))}
              {/* If number of inserted images < maxNumber allowed add image button is displayed */}
              {imageList.length < maxNumber && (
                <button
                  type="button"
                  className={styles.addImageButton}
                  onClick={() => handleOpenDialog(onImageUpload)}
                  disabled={isCompressing}
                  id="addImageButton"
                >
                  <FontAwesomeIcon
                    icon={faCamera}
                    className={styles.addImageIcon}
                  />
                </button>
              )}
            </div>
          )}
        </ImageUploading>
      </div>
      <PhotoPickerDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onPickCamera={onPickCamera}
        onPickGallery={onPickGallery}
      />
    </>
  );
};

export const ImageUploaderLimited = ({ setValue, getValues, onScroll, maxNumber }) => {
  const [images, setImages] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [userChoice, setUserChoice] = useState(null);
  const uploadRef = useRef(null);

  // Add a key to force re-render of ImageUploading component
  const [uploadKey, setUploadKey] = useState(0);

  // Load images from localStorage when component mounts
  // useEffect(() => {
  //   const savedData = JSON.parse(localStorage.getItem('formData'));
  //   if (savedData) {
  //     const compressedFiles = savedData.compressedFiles;
  //     if (compressedFiles && compressedFiles.length !== 0) {
  //       const loadedImages = compressedFiles.map((fileData, index) => ({
  //         data_url: fileData.base64, // Use base64 data
  //         file: new File([], fileData.name, {
  //           type: fileData.type,
  //           lastModified: fileData.lastModified,
  //         }),
  //         id: `${fileData.name}_${index}`,
  //         compressed: true, // Mark as already compressed
  //       }));
  //       setImages(loadedImages);
  //     }
  //   }
  // }, []);

  // Save form data to localStorage on change
  useEffect(() => {
    if (images.length > 0) {
      const compressedFiles = images.map((image) => ({
        base64: image.data_url,
        name: image.file.name,
        type: image.file.type,
        lastModified: image.file.lastModified,
      }));

      const savedData = JSON.parse(localStorage.getItem("formData")) || {};
      const updatedData = {
        ...savedData,
        compressedFiles,
      };

      localStorage.setItem("formData", JSON.stringify(updatedData));
    }
  }, [images]);

  const displayAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: message,
    });
  };

  const checkImages = (imageList) => {
    const validImages = [];
    imageList.forEach((image) => {
      const safeFilename = sanitizeFilename(image.file.name);
      if (!image.file.type.startsWith("image/")) {
        displayAlert("Le fichier sélectionné n'est pas valide");
      } else if (safeFilename !== image.file.name) {
        displayAlert("Le nom du fichier contient des caractères non valides");
      } else {
        validImages.push(image);
      }
    });
    return validImages;
  };

  const handleImageChange = async (imageList) => {
    console.log("handleImageChange called with:", imageList.length, "images");
    console.log("Current images in state:", images.length);

    // Validate all images first
    const validImages = checkImages(imageList);
    if (validImages.length !== imageList.length) {
      return; // Stop processing if there are invalid images
    }

    setIsCompressing(true);

    // Find the newly added images by comparing with the current images in state
    const newImages = imageList.filter(
      (image) =>
        !images.some(
          (img) =>
            img.file.name === image.file.name &&
            img.file.size === image.file.size
        )
    );

    console.log("New images to process:", newImages.length);

    if (newImages.length === 0) {
      setIsCompressing(false);
      return;
    }

    try {
      // Validate and compress the new images
      const base64Files = await Promise.all(
        newImages.map(async (image) => {
          if (image.compressed) {
            // If the image is already compressed, skip compression
            return image;
          }

          try {
            // Compress the image and convert it to base64
            const compressedFile = await compressImage(image.file);

            // Create a base64 representation of the compressed image
            const reader = new FileReader();
            return new Promise((resolve, reject) => {
              reader.onload = () => {
                resolve({
                  base64: reader.result, // Base64 data
                  file: compressedFile, // Compressed file object
                  compressed: true,
                });
              };
              reader.onerror = () => reject("Error reading file");
              reader.readAsDataURL(compressedFile); // Convert to base64
            });
          } catch (error) {
            console.error("Error during compression:", error);
            throw error;
          }
        })
      );

      // Add only the newly processed images to the state
      setImages((prevImages) => {
        const updatedImages = [
          ...prevImages, // Existing images
          ...base64Files.map((fileData, index) => ({
            data_url: fileData.base64, // Base64 for preview
            file: fileData.file, // Compressed file object
            id: `${fileData.file.name}_${Date.now()}_${index}`, // More unique ID
            compressed: true,
          })),
        ];

        console.log("Updated images:", updatedImages.length);

        // Update the form value with the compressed files (base64 and metadata)
        setValue("compressedFiles", [
          ...updatedImages.map((fileData) => ({
            base64: fileData.data_url,
            name: fileData.file.name,
            type: fileData.file.type,
            lastModified: fileData.file.lastModified,
          })),
        ]);

        return updatedImages;
      });
    } catch (error) {
      console.error("Error processing images:", error);
      displayAlert("Erreur lors du traitement de l'image");
    } finally {
      setIsCompressing(false);
      // Reset user choice after processing
      setUserChoice(null);
      // Force re-render of ImageUploading component
      setUploadKey((prev) => prev + 1);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.6,
        maxWidth: 800,
        maxHeight: 800,
        mimeType: "image/jpeg",
        success: (compressedFile) => resolve(compressedFile),
        error: (error) => reject(error),
      });
    });
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    const currentCompressedFiles = getValues("compressedFiles") || [];
    setValue(
      "compressedFiles",
      currentCompressedFiles.filter((_, i) => i !== index)
    );
    // Force re-render after removal
    setUploadKey((prev) => prev + 1);
  };

  const handleOpenDialog = (uploadFunction) => {
    if (isMobile) {
      uploadRef.current = uploadFunction;
      setOpenDialog(true);
    } else {
      uploadFunction();
    }
  };

  const onPickCamera = () => {
    console.log("Camera selected");
    setUserChoice("camera");
    setOpenDialog(false);
    // Small delay to ensure state is updated before triggering upload
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current();
      }
    }, 100);
  };

  const onPickGallery = () => {
    console.log("Gallery selected");
    setUserChoice("gallery");
    setOpenDialog(false);
    // Small delay to ensure state is updated before triggering upload
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current();
      }
    }, 100);
  };

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    setIsMobile(mobileRegex.test(userAgent));
  }, []);

  // Remove the automatic trigger effect - handle it directly in the picker functions
  // useEffect(() => {
  //   if(userChoice){
  //     uploadRef.current && uploadRef.current();
  //     setUserChoice(null);
  //   }
  // },[userChoice]);

  const getInputProps = () => {
    const baseProps = { accept: "image/*" };

    if (userChoice === "camera") {
      return {
        ...baseProps,
        capture: "environment", // This tells iOS to prefer the camera
      };
    }

    return baseProps;
  };

  return (
    <>
      <div
        className={classNames(
          i18n.language === "fr" ||  i18n.language === "en" ? styles.imagesField : styles.imagesFieldArabic
        )}
      >
        <ImageUploading
          key={uploadKey} // Force re-render when key changes
          multiple // To accept multiple images
          value={images}
          onChange={handleImageChange}
          maxNumber={maxNumber} // accepts maxNumber images
          dataURLKey="data_url"
          acceptType={["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "avif", "bmp", "tiff"]}
          inputProps={getInputProps()}
        >
          {({ imageList, onImageUpload, onImageRemove }) => (
            <div className={styles.imagePreviews}>
              {/* Images previews */}
              {imageList.map((image, index) => (
                <div
                  key={`${image.id || index}_${uploadKey}`}
                  className={styles.buttonContainer}
                >
                  <div className={styles.imageButton}>
                    <img
                      src={image.data_url}
                      alt={`Preview ${index}`}
                      className={styles.previewImage}
                    />
                    {isCompressing && (
                      <div className={styles.loadingOverlay}>
                        <FontAwesomeIcon
                          icon={faSpinner}
                          className={styles.spinnerIcon}
                          spin
                        />
                      </div>
                    )}
                    {/* Remove an inserted image */}
                    <span
                      className={styles.removeBtn}
                      onClick={() => handleRemoveImage(index)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </span>
                  </div>
                </div>
              ))}
              {/* If number of inserted images < maxNumber allowed add image button is displayed */}
              {imageList.length < maxNumber && (
                <button
                  type="button"
                  className={styles.addImageButton}
                  onClick={() => handleOpenDialog(onImageUpload)}
                  disabled={isCompressing}
                  id="addImageButton"
                >
                  <FontAwesomeIcon
                    icon={faCamera}
                    className={styles.addImageIcon}
                  />
                </button>
              )}
            </div>
          )}
        </ImageUploading>
      </div>
      <PhotoPickerDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onPickCamera={onPickCamera}
        onPickGallery={onPickGallery}
      />
    </>
  );
};

export const ImageUploaderC = ({ setValue, getValues, onScroll }) => {
  const [images, setImages] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const compressCancelFlags = useRef({});
  const maxNumber = 2; // Max number of images allowed
  const [openDialog, setOpenDialog] = useState(false);
  const [userChoice, setUserChoice] = useState(null);
  const uploadRef = useRef(null);

  // Add a key to force re-render of ImageUploading component
  const [uploadKey, setUploadKey] = useState(0);

  // Load images from localStorage when component mounts
  // useEffect(() => {
  //   const savedData = JSON.parse(localStorage.getItem('formDataC'));
  //   if (savedData) {
  //     const compressedFiles = savedData.compressedFiles;
  //     if (compressedFiles && compressedFiles.length !== 0) {
  //       const loadedImages = compressedFiles.map((fileData, index) => ({
  //         data_url: fileData.base64, // Use base64 data
  //         file: new File([], fileData.name, {
  //           type: fileData.type,
  //           lastModified: fileData.lastModified,
  //         }),
  //         id: `${fileData.name}_${index}`,
  //       }));
  //       setImages(loadedImages);
  //     }
  //   }
  // }, []);

  // Save form data to localStorage on change
  useEffect(() => {
    if (images.length > 0) {
      // Prepare images with base64 data for saving
      const compressedFiles = images.map((image) => ({
        base64: image.data_url, // Save base64 data
        name: image.file.name,
        type: image.file.type,
        lastModified: image.file.lastModified,
      }));

      // Merge the new compressedLot with the existing formData
      const savedData = JSON.parse(localStorage.getItem("formDataC")) || {};
      const updatedData = {
        ...savedData,
        compressedFiles, // Update only compressedLot, keep other data
      };

      localStorage.setItem("formDataC", JSON.stringify(updatedData));
    }
  }, [images]);

  const displayAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: message,
    });
  };

  const checkImages = (imageList) => {
    const validImages = [];
    imageList.forEach((image) => {
      const safeFilename = sanitizeFilename(image.file.name);
      if (!image.file.type.startsWith("image/")) {
        displayAlert("Le fichier sélectionné n'est pas valide");
      } else if (safeFilename !== image.file.name) {
        displayAlert("Le nom du fichier contient des caractères non valides");
      } else {
        validImages.push(image);
      }
    });
    return validImages;
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.6,
        maxWidth: 800,
        maxHeight: 800,
        mimeType: "image/jpeg",
        success: (compressedFile) => resolve(compressedFile),
        error: (error) => reject(error),
      });
    });
  };

  const handleImageChange = async (imageList) => {
    console.log("handleImageChange called with:", imageList.length, "images");
    console.log("Current images in state:", images.length);
    console.log("is compressing");

    // Validate all images first
    const validImages = checkImages(imageList);
    if (validImages.length !== imageList.length) {
      return; // Stop processing if there are invalid images
    }

    setIsCompressing(true);

    // Find the newly added images by comparing with the current images in state
    const newImages = imageList.filter(
      (image) =>
        !images.some(
          (img) =>
            img.file.name === image.file.name &&
            img.file.size === image.file.size
        )
    );

    console.log("New images to process:", newImages.length);

    if (newImages.length === 0) {
      setIsCompressing(false);
      return;
    }

    try {
      // Validate and compress the new images
      const base64Files = await Promise.all(
        newImages.map(async (image) => {
          if (image.compressed) {
            // If the image is already compressed, skip compression
            return image;
          }

          try {
            // Compress the image and convert it to base64
            const compressedFile = await compressImage(image.file);

            // Create a base64 representation of the compressed image
            const reader = new FileReader();
            return new Promise((resolve, reject) => {
              reader.onload = () => {
                resolve({
                  base64: reader.result, // Base64 data
                  file: compressedFile, // Compressed file object
                  compressed: true,
                });
              };
              reader.onerror = () => reject("Error reading file");
              reader.readAsDataURL(compressedFile); // Convert to base64
            });
          } catch (error) {
            console.error("Error during compression:", error);
            throw error;
          }
        })
      );

      // Add only the newly processed images to the state
      setImages((prevImages) => {
        const updatedImages = [
          ...prevImages, // Existing images
          ...base64Files.map((fileData, index) => ({
            data_url: fileData.base64, // Base64 for preview
            file: fileData.file, // Compressed file object
            id: `${fileData.file.name}_${Date.now()}_${index}`, // More unique ID
            compressed: true,
          })),
        ];

        console.log("Updated images:", updatedImages.length);

        // Update the form value with the compressed files (base64 and metadata)
        setValue("compressedFiles", [
          ...updatedImages.map((fileData) => ({
            base64: fileData.data_url,
            name: fileData.file.name,
            type: fileData.file.type,
            lastModified: fileData.file.lastModified,
          })),
        ]);

        return updatedImages;
      });
    } catch (error) {
      console.error("Error processing images:", error);
      displayAlert("Erreur lors du traitement de l'image");
    } finally {
      setIsCompressing(false);
      console.log("compressed");
      // Reset user choice after processing
      setUserChoice(null);
      // Force re-render of ImageUploading component
      setUploadKey((prev) => prev + 1);
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    const currentCompressedFiles = getValues("compressedFiles") || [];
    setValue(
      "compressedFiles",
      currentCompressedFiles.filter((_, i) => i !== index)
    );
    // Force re-render after removal
    setUploadKey((prev) => prev + 1);
  };

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    setIsMobile(mobileRegex.test(userAgent));
  }, []);

  // Remove the automatic trigger effect - handle it directly in the picker functions
  // useEffect(() => {
  //   if (userChoice) {
  //     uploadRef.current && uploadRef.current();
  //     setUserChoice(null);
  //   }
  // }, [userChoice]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const companyName = localStorage.getItem("selectedCompany");

  const handleOpenDialog = (uploadFunction) => {
    if (isMobile) {
      uploadRef.current = uploadFunction;
      setOpenDialog(true);
    } else {
      uploadFunction();
    }
  };

  const onPickCamera = () => {
    console.log("Camera selected");
    setUserChoice("camera");
    setOpenDialog(false);
    // Small delay to ensure state is updated before triggering upload
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current();
      }
    }, 100);
  };

  const onPickGallery = () => {
    console.log("Gallery selected");
    setUserChoice("gallery");
    setOpenDialog(false);
    // Small delay to ensure state is updated before triggering upload
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current();
      }
    }, 100);
  };

  const getInputProps = () => {
    const baseProps = { accept: "image/*" };

    if (userChoice === "camera") {
      return {
        ...baseProps,
        capture: "environment", // This tells iOS to prefer the camera
      };
    }

    return baseProps;
  };

  return (
    <>
      <div
        className={classNames(
          i18n.language === "fr" ? styles.imagesField : styles.imagesFieldArabic
        )}
      >
        <ImageUploading
          key={uploadKey} // Force re-render when key changes
          multiple
          value={images}
          onChange={handleImageChange}
          maxNumber={maxNumber}
          dataURLKey="data_url"
          acceptType={["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "avif", "bmp", "tiff"]}
          inputProps={getInputProps()}
        >
          {({ imageList, onImageUpload, onImageRemove }) => (
            <div className={styles.imagePreviews}>
              {imageList.map((image, index) => (
                <div
                  key={`${image.id || index}_${uploadKey}`}
                  className={styles.buttonContainer}
                  {...(selectedCompany && {
                    style: {
                      border: selectedCompany.colors.inputBorder,
                    },
                  })}
                >
                  <div className={styles.imageButton}>
                    <img
                      src={image.data_url}
                      alt={`Preview ${index}`}
                      className={styles.previewImage}
                    />
                    {isCompressing && (
                      <div className={styles.loadingOverlay}>
                        <FontAwesomeIcon
                          icon={faSpinner}
                          className={styles.spinnerIcon}
                          spin
                        />
                      </div>
                    )}
                    <span
                      className={styles.removeBtn}
                      onClick={() => handleRemoveImage(index)}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </span>
                  </div>
                </div>
              ))}
              {imageList.length < maxNumber && (
                <button
                  type="button"
                  className={styles.addImageButton}
                  onClick={() => handleOpenDialog(onImageUpload)}
                  disabled={isCompressing}
                  {...(selectedCompany && {
                    style: {
                      border: selectedCompany.colors.inputBorder,
                    },
                  })}
                >
                  <FontAwesomeIcon
                    icon={faCamera}
                    className={styles.addImageIcon}
                    {...(selectedCompany && {
                      style: {
                        color: selectedCompany.colors.iconColor,
                      },
                    })}
                  />
                </button>
              )}
            </div>
          )}
        </ImageUploading>
      </div>
      <PhotoPickerDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onPickCamera={onPickCamera}
        onPickGallery={onPickGallery}
      />
    </>
  );
};